import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchClient } from '@eureca/backoffice-api-sdk';
import { Badge, Button, cn, Separator, Skeleton, toast } from '@eureca/ui';
import { TriangleAlertIcon } from '@eureca/ui/icons';
import { isAdmin } from '@/auth/roles';

export const Route = createFileRoute('/_auth/_shell/dlq')({
  // EU-749 — esconder o item do menu não basta: a rota é alcançável digitando a
  // URL, e sem o guard a tela renderiza "0 pendentes" em cima do 403 do Core
  // (uma DLQ falsamente vazia). Mesmo padrão da `auditoria` e do catálogo; a
  // recusa definitiva segue sendo o 403 do Core.
  beforeLoad: () => {
    if (!isAdmin()) throw redirect({ to: '/inicio' });
  },
  component: DlqConsolePage,
});

interface DlqSummary {
  byStatus: { status: string; n: number }[];
  pendingByQueue: { original_queue: string | null; n: number }[];
}

interface DlqMessage {
  id: string;
  originalExchange: string;
  originalRoutingKey: string;
  originalQueue: string | null;
  reason: string | null;
  deathCount: number;
  payload: string;
  correlationId: string | null;
  eventType: string | null;
  capturedAt: string;
  status: string;
}

const DLQ_KEY = ['backoffice', 'dlq'] as const;

async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  // fetchClient throws on non-2xx (react-query surfaces it) → just unwrap data.
  const res = await fetchClient<T>({ method: 'GET', url, ...(params ? { params } : {}) });
  return res.data;
}

async function post<T>(url: string, data: Record<string, unknown>): Promise<T> {
  const res = await fetchClient<T>({ method: 'POST', url, data });
  return res.data;
}

function DlqConsolePage() {
  const qc = useQueryClient();
  const [queue, setQueue] = useState<string | null>(null);

  const summary = useQuery({
    queryKey: [...DLQ_KEY, 'summary'],
    queryFn: () => get<DlqSummary>('/dlq'),
    staleTime: 15_000,
  });

  const messages = useQuery({
    queryKey: [...DLQ_KEY, 'messages', queue],
    queryFn: () =>
      get<{ messages: DlqMessage[] }>('/dlq/messages', {
        status: 'pending',
        ...(queue ? { queue } : {}),
      }).then((r) => r.messages),
    staleTime: 15_000,
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: DLQ_KEY });

  const reprocess = useMutation({
    mutationFn: (id: string) => post(`/dlq/messages/${id}/reprocess`, {}),
    onSuccess: () => {
      toast.success('Reprocessado — republicado na origem');
      invalidate();
    },
    onError: () => toast.error('Falha ao reprocessar'),
  });

  const discard = useMutation({
    mutationFn: (id: string) => post(`/dlq/messages/${id}/discard`, {}),
    onSuccess: () => {
      toast.success('Descartado');
      invalidate();
    },
    onError: () => toast.error('Falha ao descartar'),
  });

  const batch = useMutation({
    mutationFn: (q: string | null) =>
      post<{ reprocessed: number }>('/dlq/reprocess-batch', {
        ...(q ? { queue: q } : {}),
        limit: 100,
      }),
    onSuccess: (r) => {
      toast.success(`Lote: ${r.reprocessed} reprocessado(s)`);
      invalidate();
    },
    onError: () => toast.error('Falha no lote'),
  });

  const busy = reprocess.isPending || discard.isPending || batch.isPending;
  const totalPending = summary.data?.byStatus.find((s) => s.status === 'pending')?.n ?? 0;

  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <TriangleAlertIcon className="size-5 text-muted-foreground" />
          <h1 className="text-xl font-semibold text-foreground">Dead-letter queue</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Mensagens que falharam e caíram na DLQ (`eureca.failed`). Reprocessar republica na origem
          (exchange/routing-key), opcionalmente após corrigir a causa.
        </p>
      </header>

      {/* Resumo por fila (clicável = filtro) */}
      <section className="flex flex-col gap-2">
        <div className="flex flex-row flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-foreground">
            {totalPending} pendente{totalPending === 1 ? '' : 's'}
          </span>
          <FilterChip active={queue === null} onClick={() => setQueue(null)} label="Todas" />
          {summary.isLoading ? (
            <Skeleton className="h-6 w-40" />
          ) : (
            summary.data?.pendingByQueue.map((q) => (
              <FilterChip
                key={q.original_queue ?? 'null'}
                active={queue === q.original_queue}
                onClick={() => setQueue(q.original_queue)}
                label={`${q.original_queue ?? '(sem fila)'} · ${q.n}`}
              />
            ))
          )}
        </div>
        {queue ? (
          <div>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => batch.mutate(queue)}>
              Reprocessar lote ({queue})
            </Button>
          </div>
        ) : null}
      </section>

      <Separator />

      {/* Lista de mensagens */}
      <section className="flex flex-col gap-2">
        {messages.isLoading ? (
          <>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl" />
            ))}
          </>
        ) : messages.isError ? (
          <p className="text-sm text-destructive">Falha ao carregar mensagens.</p>
        ) : (messages.data?.length ?? 0) === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Nenhuma mensagem pendente.
          </p>
        ) : (
          messages.data?.map((m) => (
            <MessageCard
              key={m.id}
              m={m}
              disabled={busy}
              onReprocess={() => reprocess.mutate(m.id)}
              onDiscard={() => discard.mutate(m.id)}
            />
          ))
        )}
      </section>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border text-muted-foreground hover:bg-muted'
      )}
    >
      {label}
    </button>
  );
}

function MessageCard({
  m,
  disabled,
  onReprocess,
  onDiscard,
}: {
  m: DlqMessage;
  disabled: boolean;
  onReprocess: () => void;
  onDiscard: () => void;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-4">
      <div className="flex flex-row flex-wrap items-center gap-2">
        <Badge variant="secondary">{m.originalQueue ?? '(sem fila)'}</Badge>
        <span className="text-xs text-muted-foreground">
          {m.originalExchange} → {m.originalRoutingKey || '(vazio)'}
        </span>
        {m.reason ? <Badge variant="outline">{m.reason}</Badge> : null}
        {m.deathCount > 1 ? <Badge variant="outline">×{m.deathCount}</Badge> : null}
      </div>
      {m.correlationId ? (
        <p className="text-xs text-muted-foreground">
          correlationId (buscar no Grafana/Loki):{' '}
          <code className="rounded bg-muted px-1 py-0.5 font-mono text-foreground">
            {m.correlationId}
          </code>
        </p>
      ) : null}
      <pre className="max-h-32 overflow-auto rounded-lg bg-muted p-2 text-xs text-foreground">
        {m.payload}
      </pre>
      <div className="flex flex-row items-center gap-2">
        <Button size="sm" disabled={disabled} onClick={onReprocess}>
          Reprocessar
        </Button>
        <Button size="sm" variant="outline" disabled={disabled} onClick={onDiscard}>
          Descartar
        </Button>
      </div>
    </div>
  );
}
